<script>
  import { format } from 'svelte-i18n';

  import { Porthole } from '@client/components/porthole';
  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';

  import { UsersService } from '@client/services/users';

  import { userStore } from '@client/stores/user';

  import { userMetadata } from '@client/utils/users';

  import { USER_REQUEST_PARAMS } from '@client/enums';

  import { generatePastelColors } from '@common/utils/colors';
  import { debounce } from '@common/utils/debounce';

  export let group = { senders: [] };
  export let active = false;
  export let users = { data: [] };
  export let onChange = () => {};
  export let exclusions = false;
  export let loading = false;

  let totalUsers = 0;
  let searching = false;
  let pendingSearches = 0;

  let items = [];
  let originalItems = [];
  let selected = [];
  let placeholder = exclusions
    ? $format('label.EXCLUDED.USERS_PLACEHOLDER')
    : $format('label.INCLUDED.USERS_PLACEHOLDER');
  let header = exclusions ? $format('label.EXCLUDED.USERS') : $format('label.INCLUDED.USERS');
  let type = exclusions ? 'senderexclusions' : 'senders';
  let newItem = '';
  let previousFilterText = '';
  let processedUsersData;

  const colors = generatePastelColors(25000);

  const processUsers = (usersList) => {
    const list = usersList
      .map((user) => {
        const username = user.email.split('@').shift();
        const meta = userMetadata({ ...user, features: $userStore.features });

        const item = {
          name: meta.name,
          email: meta.email,
          username,
          color: colors.shift(),
          label: meta.name,
          value: username,
          checked: selected?.includes(username)
        };

        if (!originalItems.some((existingItem) => existingItem.email === item.email)) {
          originalItems.push(item);
        }

        return item;
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    const all = {
      email: $format('label.ENTIRE_COMPANY'),
      name: $format('label.ALL_USERS'),
      color: colors.shift(),
      username: '*',
      label: $format('label.ALL_USERS'),
      value: '*',
      checked: selected?.includes('*')
    };

    list.unshift(all);

    originalItems = [...originalItems].sort((a, b) => a.label.localeCompare(b.label));

    if (!originalItems.some((existingItem) => existingItem.email === all.email)) {
      originalItems.unshift(all);
    }

    return list;
  };

  const searchUsers = debounce(async () => {
    if (!newItem.length) {
      items = [...originalItems];
      return;
    }

    // Only the first page of users is loaded, so a local substring hit can't rule out server matches.
    if (newItem === previousFilterText) {
      return;
    }

    const query = newItem;

    previousFilterText = query;
    pendingSearches += 1;
    searching = true;

    try {
      const users = await UsersService.getUsers({
        ...USER_REQUEST_PARAMS,
        filterByDate: false,
        query,
        perPage: 100
      });

      const results = processUsers(users.data || []);

      // Reassign rather than push: Select only re-filters when the array reference changes.
      items = [...items, ...results.filter((user) => !items.some(({ value }) => value === user.value))];
      originalItems = [
        ...originalItems,
        ...results.filter((user) => !originalItems.some(({ value }) => value === user.value))
      ];
    } finally {
      pendingSearches -= 1;
      searching = pendingSearches > 0;
    }
  }, 300);

  const loadUsers = () => {
    selected = group[type].map(({ username }) => username).sort();
    items = processUsers(users.data || []);
  };

  const onClear = ({ detail }) => {
    if (detail?.length || !selected?.length) {
      selected = [];
    } else {
      selected = selected?.filter(({ value }) => value !== detail.value);
    }

    onSelect({ detail: selected || [] });
  };

  const processNewUser = (newUser) => {
    const username = newUser.trim().split('@').shift();
    const existingSelection = selected?.find(({ value }) => value === username);
    const existingUser = items?.find(({ value }) => value === username);

    if (username.length) {
      const userEmail = `${username}@${$userStore.domain}`;

      const item = {
        color: generatePastelColors(1).shift(),
        email: userEmail,
        name: username,
        username,
        value: username,
        label: username,
        checked: true
      };

      if (!existingSelection) {
        selected.push(item);
      }

      if (!existingUser) {
        items.push(item);
      }
    }
  };

  const updateUserSelections = () => {
    items = [...items];
    selected = [...selected];

    newItem = '';

    onSelect({ detail: selected });
  };

  const onKeyUp = (e) => {
    if (e.key === 'Enter') {
      if (!selected.find(({ value }) => value === newItem)) {
        processNewUser(newItem);
        updateUserSelections();
      }
    }
  };

  const onPaste = (e) => {
    e.preventDefault();

    const text = e.clipboardData.getData('text');
    const emails = text.split(/[\s,]+/).filter(Boolean);

    if (!emails.length) {
      return;
    }

    if (!selected) {
      selected = [];
    }

    emails.forEach((email) => processNewUser(email.trim()));

    updateUserSelections();
  };

  const onSelect = ({ detail = [] }) => {
    onChange(
      detail.map((item) => (item.username = item.value) && item),
      type
    );
  };

  let itemFilter = (label, text, option) => {
    const { name, email } = option;
    return JSON.stringify({ name, email }).toLowerCase().indexOf(text.toLowerCase()) > -1;
  };

  $: count = selected?.length || 0;
  $: active = count > 0;
  $: totalUsers = loading ? 0 : users?.headers ? Number(users.headers['x-filtered-total']) + 1 : items.length || 1;
  $: if (!loading && users?.data !== processedUsersData) {
    processedUsersData = users?.data;
    loadUsers();
  }
</script>

<div class="header">{header} ({count}/{totalUsers})</div>

<span on:keyup={onKeyUp} on:paste={onPaste} role="button" tabindex="0">
  <Select
    bind:items
    bind:value={selected}
    on:change={onSelect}
    on:clear={onClear}
    bind:filterText={newItem}
    on:filter={searchUsers}
    {itemFilter}
    {placeholder}
    class="select-element"
    searchable
    multiple
    {loading}
  >
    <svelte:fragment slot="item" let:item>
      <div class="item">
        {#key item.email}
          <Porthole details={item} color={item.color} />
        {/key}
        <div class="item-detail">
          <div class="name">{item.name}</div>
          <div class="email">{item.email}</div>
        </div>
      </div>
    </svelte:fragment>
    <slot name="empty" slot="empty">
      <div class="empty">
        {#if searching}
          <Spinner show />
        {:else if newItem.length}
          {#if !selected.find(({ value }) => value === newItem)}
            {@html $format('label.PRESS_ENTER_TO_ADD_USER', { values: { item: newItem } })}
          {:else}
            {@html $format('label.USER_ALREADY_SELECTED', { values: { item: newItem } })}
          {/if}
        {:else}
          {$format('label.SEARCH_USERS_NAME_EMAIL')}
        {/if}
      </div>
    </slot>
  </Select>
</span>

<style lang="scss" src="./users-selector.scss"></style>
